import { Button } from '@/components/form/Button';
import { useModal } from '@/components/modal/useModal';
import { Label } from '@/components/text/Label';
import SiChevronLeft from '@/icons/SiChevronLeft';
import SiClose from '@/icons/SiClose';
import SiEdit from '@/icons/SiEdit';
import { Page } from '@/layouts/Page';
import { cn } from '@/lib/utils';
import { ManageBaseCatalogueGlobalMarginModal } from '@/pages/msps/ManageBaseCatalogueGlobalMarginModal';
import { useFilterPack, useMspBaseCatalogue } from '@/services/api/hooks/catalog';
import { RoleType } from '@/services/authentication/hooks/user';
import { useCatalogueService } from '@/services/catalogue/useCatalogueService';
import { FC, HTMLAttributes, PropsWithChildren, useMemo } from 'react';
import LocalizedStrings from 'react-localization';
import { useNavigate, useParams } from 'react-router';
import { ImpersonationBanner } from '../ImpersonationBanner';
import { CreateFilterModal } from './CreateFilterModal';
import DeleteIndividualProductFilterModal from './DeleteIndividualProductFilterModal';
import DeleteMarginFilterModal from './DeleteMarginFilterModal';
import DeleteProductFilterModal from './DeleteProductFilterModal';
import { ManageGlobalMarginModal } from './ManageGlobalMarginModal';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { organiseFilters } from './organiseFilters';

const GlobalFilterIcon = () => (
  <svg width="37" height="24" viewBox="0 0 37 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M32.25 12C32.25 16.0041 29.0041 19.25 25 19.25C20.9959 19.25 17.75 16.0041 17.75 12C17.75 7.99594 20.9959 4.75 25 4.75C29.0041 4.75 32.25 7.99594 32.25 12Z"
      fill="#F3FCFE"
      stroke="#0C3F47"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M22.25 5.5V9.25C22.25 10.9069 20.9068 12.25 19.25 12.25H18"
      stroke="#0C3F47"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M23 18.5C23 18.5 23.75 17 23.75 15C23.75 12.8501 26.1535 14.0281 26.8267 10.7433C27.0484 9.6612 27.8954 8.75 29 8.75H31"
      stroke="#0C3F47"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M22 12L17.5 7V17L22 12Z" fill="#F3FCFE" />
    <path
      d="M13.2499 7.00002L12.7059 6.45702C12.2536 6.00501 11.6404 5.7511 11.0009 5.7511C10.3615 5.7511 9.74821 6.00501 9.29592 6.45702L5.45692 10.294C5.2328 10.518 5.05502 10.784 4.93373 11.0767C4.81243 11.3694 4.75 11.6832 4.75 12C4.75 12.3169 4.81243 12.6306 4.93373 12.9233C5.05502 13.2161 5.2328 13.482 5.45692 13.706L9.29492 17.544C10.2369 18.485 11.7649 18.485 12.7059 17.544L13.2499 17M19.2499 12H11.7499H19.2499ZM19.2499 12L16.7499 9.75002L19.2499 12ZM19.2499 12L16.7499 14.25L19.2499 12Z"
      fill="#F3FCFE"
    />
    <path
      d="M13.2499 7.00002L12.7059 6.45702C12.2536 6.00501 11.6404 5.7511 11.0009 5.7511C10.3615 5.7511 9.74821 6.00501 9.29592 6.45702L5.45692 10.294C5.2328 10.518 5.05502 10.784 4.93373 11.0767C4.81243 11.3694 4.75 11.6832 4.75 12C4.75 12.3169 4.81243 12.6306 4.93373 12.9233C5.05502 13.2161 5.2328 13.482 5.45692 13.706L9.29492 17.544C10.2369 18.485 11.7649 18.485 12.7059 17.544L13.2499 17M19.2499 12H11.7499M19.2499 12L16.7499 9.75002M19.2499 12L16.7499 14.25"
      stroke="#0C3F47"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const FilterIcon = () => (
  <svg width="37" height="24" viewBox="0 0 37 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M32.25 4.75H17.75L22.3117 10.4522C22.5954 10.8068 22.75 11.2474 22.75 11.7016V18.25C22.75 18.8023 23.1977 19.25 23.75 19.25H26.25C26.8023 19.25 27.25 18.8023 27.25 18.25V11.7016C27.25 11.2474 27.4046 10.8068 27.6883 10.4522L32.25 4.75Z"
      fill="#F3FCFE"
      stroke="#0C3F47"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M22 12L17.5 7V17L22 12Z" fill="#F3FCFE" />
    <path
      d="M13.2499 7.00002L12.7059 6.45702C12.2536 6.00501 11.6404 5.7511 11.0009 5.7511C10.3615 5.7511 9.74821 6.00501 9.29592 6.45702L5.45692 10.294C5.2328 10.518 5.05502 10.784 4.93373 11.0767C4.81243 11.3694 4.75 11.6832 4.75 12C4.75 12.3169 4.81243 12.6306 4.93373 12.9233C5.05502 13.2161 5.2328 13.482 5.45692 13.706L9.29492 17.544C10.2369 18.485 11.7649 18.485 12.7059 17.544L13.2499 17M19.2499 12H11.7499H19.2499ZM19.2499 12L16.7499 9.75002L19.2499 12ZM19.2499 12L16.7499 14.25L19.2499 12Z"
      fill="#F3FCFE"
    />
    <path
      d="M13.2499 7.00002L12.7059 6.45702C12.2536 6.00501 11.6404 5.7511 11.0009 5.7511C10.3615 5.7511 9.74821 6.00501 9.29592 6.45702L5.45692 10.294C5.2328 10.518 5.05502 10.784 4.93373 11.0767C4.81243 11.3694 4.75 11.6832 4.75 12C4.75 12.3169 4.81243 12.6306 4.93373 12.9233C5.05502 13.2161 5.2328 13.482 5.45692 13.706L9.29492 17.544C10.2369 18.485 11.7649 18.485 12.7059 17.544L13.2499 17M19.2499 12H11.7499M19.2499 12L16.7499 9.75002M19.2499 12L16.7499 14.25"
      stroke="#0C3F47"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ProductFilterIcon = () => (
  <svg width="40" height="24" viewBox="0 0 40 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M33.2502 19.25H22.75C21.6454 19.25 20.75 18.3546 20.75 17.25V9.63478C20.75 9.22174 20.8779 8.81884 21.1161 8.48141L23.75 4.75H32.2502L34.8842 8.48141C35.1224 8.81884 35.2502 9.22174 35.2502 9.63478V17.25C35.2502 18.3546 34.3548 19.25 33.2502 19.25Z"
      stroke="#0C3F47"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M21 9.25H35" stroke="#0C3F47" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M28 5V9" stroke="#0C3F47" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M22 12L17.5 7V17L22 12Z" fill="#F3FCFE" />
    <path
      d="M13.2499 7.00002L12.7059 6.45702C12.2536 6.00501 11.6404 5.7511 11.0009 5.7511C10.3615 5.7511 9.74821 6.00501 9.29592 6.45702L5.45692 10.294C5.2328 10.518 5.05502 10.784 4.93373 11.0767C4.81243 11.3694 4.75 11.6832 4.75 12C4.75 12.3169 4.81243 12.6306 4.93373 12.9233C5.05502 13.2161 5.2328 13.482 5.45692 13.706L9.29492 17.544C10.2369 18.485 11.7649 18.485 12.7059 17.544L13.2499 17M19.2499 12H11.7499H19.2499ZM19.2499 12L16.7499 9.75002L19.2499 12ZM19.2499 12L16.7499 14.25L19.2499 12Z"
      fill="#F3FCFE"
    />
    <path
      d="M13.2499 7.00002L12.7059 6.45702C12.2536 6.00501 11.6404 5.7511 11.0009 5.7511C10.3615 5.7511 9.74821 6.00501 9.29592 6.45702L5.45692 10.294C5.2328 10.518 5.05502 10.784 4.93373 11.0767C4.81243 11.3694 4.75 11.6832 4.75 12C4.75 12.3169 4.81243 12.6306 4.93373 12.9233C5.05502 13.2161 5.2328 13.482 5.45692 13.706L9.29492 17.544C10.2369 18.485 11.7649 18.485 12.7059 17.544L13.2499 17M19.2499 12H11.7499M19.2499 12L16.7499 9.75002M19.2499 12L16.7499 14.25"
      stroke="#0C3F47"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);



const Box: FC<PropsWithChildren<HTMLAttributes<HTMLDivElement>>> = ({ children, className, ...props }) => (
  <div
    {...props}
    className={cn(
      `flex w-full items-center gap-2 rounded-md border border-[#DDEDF0] bg-[#F3FCFE] px-4 py-3`,
      className
    )}
  >
    {children}
  </div>
);

export const Criteria: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center">
    <div className="bg-primary-700 border-primary-700 relative z-10 flex items-center justify-center gap-1 overflow-clip rounded-lg border py-1.5 px-4 text-sm text-white">
      <span>{label}</span>
    </div>
    <div className="relative -left-2 flex items-center justify-center rounded-r-lg border border-l-0 border-gray-200 py-1.5 pr-3 pl-5 text-sm">
      {value}
    </div>
  </div>
);

const strings = new LocalizedStrings({
  en: {
    manageFilters: 'Manage filters',
    createAndApplyFilter: 'Create & apply filter',
    globalMargin: 'Global margin',
    mspDiscount: 'MSP Discount',
    editDiscount: 'Edit MSP discount',
    editGlobalMargin: 'Edit global margin',
    marginFilters: 'Margin filters',
    productFilters: 'Visibility filters',
    globalMarginExplanation: `This margin option is always transparently applied to this MSP's margin. This is never overridden by other filters. It's useful if you want to apply an extra margin to all products if, for example, they are an international customer or have additional costs associated with them. It's also useful for applying a discount to all products, e.g for a promotion or special relationship.`,
    adminOptions: 'Admin options',
    adminOptionsExplanation: `These are options that you can make as an administrator but can't be seen or edited by the MSP.`,
    delete: 'Delete'
  }
});

const MSPAdminSettings = () => {
  const { partnerId } = useParams<'partnerId'>();
  if (!partnerId) throw new Error('Missing msp id');

  const [baseCatalogue] = useMspBaseCatalogue(partnerId);

  const manageBaseCatalogueGlobalMarginModalProps = useModal<{ mspId: string }>();
  const onMspManageBaseCatalogueGlobalMargin = () =>
    manageBaseCatalogueGlobalMarginModalProps.open({ mspId: partnerId });

  return (
    <div className="flex flex-col gap-2">
      <ManageBaseCatalogueGlobalMarginModal {...manageBaseCatalogueGlobalMarginModalProps} />
      <div className="flex flex-col gap-3 p-4 px-4 border border-[#DDEDF0] bg-[#fcfeff] rounded-lg">
        <div>
          <label className="text-secondary-std text-lg font-medium">{strings.adminOptions}</label>
          <p className="text-sm">{strings.adminOptionsExplanation}</p>
        </div>
        <hr />
        <div>
          <Label>{strings.mspDiscount}</Label>
          <p className="text-sm">{strings.globalMarginExplanation}</p>
        </div>
        <Box className="flex justify-between items-center">
          <div className="flex items-center">
            <GlobalFilterIcon />
            <p className="font-medium">{baseCatalogue.filterPack.globalMarginModifier}%</p>
          </div>
          <Button variant={ButtonVariant.Ghost} onClick={onMspManageBaseCatalogueGlobalMargin}>
            <SiEdit className="text-xl" />
            <span>{strings.editDiscount}</span>
          </Button>
        </Box>
      </div>
    </div>
  );
};

export const ManageFilters = () => {
  const ManageFilters = () => {
    const navigate = useNavigate();
    const [filterPackId, configuration] = useCatalogueService(state => [state.filterPackId, state.configuration]);
    if (!filterPackId) throw new Error('Missing filter pack id');
    const [filterPack] = useFilterPack(filterPackId);
    const pack = useMemo(() => organiseFilters(filterPack), [filterPack]);

    const createFilterModalProps = useModal();
    const manageGlobalMarginModalProps = useModal();
    const deleteMarginFilterModalProps = useModal<{ marginFilterId: string }>();
    const deleteProductFilterModalProps = useModal<{ productFilterId: string; filteredByManufacturer?: boolean }>();
    const deleteIndividualProductFilterModalProps = useModal<{
      productFilterId: string;
      filteredByMargin?: boolean;
    }>();

    return (
      <div className="flex flex-col gap-4">
        <CreateFilterModal {...createFilterModalProps} />
        <ManageGlobalMarginModal {...manageGlobalMarginModalProps} />
        <DeleteMarginFilterModal {...deleteMarginFilterModalProps} />
        <DeleteProductFilterModal {...deleteProductFilterModalProps} />
        <DeleteIndividualProductFilterModal {...deleteIndividualProductFilterModalProps} />

        <div className="flex w-full items-center justify-between">
          <h2 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
            <Button variant={ButtonVariant.Ghost} onClick={() => navigate(-1)}>
              <SiChevronLeft className="text-4xl" />
            </Button>
            <span>{strings.manageFilters}</span>
          </h2>
          <div className="flex gap-2">
            <ImpersonationBanner />

            <Button variant={ButtonVariant.Primary} onClick={() => createFilterModalProps.open()}>
              {strings.createAndApplyFilter}
            </Button>
          </div>
        </div>
        {configuration.as === RoleType.Admin && configuration.for === RoleType.Partner && <MSPAdminSettings />}
        <div className="flex flex-col gap-3">
          <Label>{strings.globalMargin}</Label>
          <Box className="flex justify-between items-center">
            <div className="flex items-center">
              <GlobalFilterIcon />
              <p className="font-medium">{pack.global.margin}%</p>
            </div>
            <Button variant={ButtonVariant.Ghost} onClick={() => manageGlobalMarginModalProps.open()}>
              <SiEdit className="text-xl" />
              <span>{strings.editGlobalMargin}</span>
            </Button>
          </Box>
          <Label>{strings.marginFilters}</Label>
          {[...pack.composite.margin].map(filter => (
            <Box className="flex justify-between items-center" key={filter.id}>
              <div className="flex items-center">
                <FilterIcon />
                {filter.category && <Criteria label="Category" value={filter.category?.name} />}
                {filter.manufacturer && <Criteria label="Manufacturer" value={filter.manufacturer?.name} />}
                {filter.modifier && <p className="font-medium">{filter.modifier}%</p>}
              </div>
              <Button
                variant={ButtonVariant.DesctructiveGhost}
                onClick={() => deleteMarginFilterModalProps.open({ marginFilterId: filter.id })}
              >
                <SiClose className="text-xl" />
                <span>{strings.delete}</span>
              </Button>
            </Box>
          ))}
          {[...pack.category.margin].map(filter => (
            <Box className="flex justify-between items-center" key={filter.id}>
              <div className="flex items-center">
                <FilterIcon />
                {filter.category && <Criteria label="Category" value={filter.category?.name} />}
                {filter.modifier && <p className="font-medium">{filter.modifier}%</p>}
              </div>
              <Button
                variant={ButtonVariant.DesctructiveGhost}
                onClick={() => deleteMarginFilterModalProps.open({ marginFilterId: filter.id })}
              >
                <SiClose className="text-xl" />
                <span>{strings.delete}</span>
              </Button>
            </Box>
          ))}
          {[...pack.manufacturer.margin].map(filter => (
            <Box className="flex justify-between items-center" key={filter.id}>
              <div className="flex items-center">
                <FilterIcon />
                {filter.manufacturer && <Criteria label="Manufacturer" value={filter.manufacturer?.name} />}
                {filter.modifier && <p className="font-medium">{filter.modifier}%</p>}
              </div>
              <Button
                variant={ButtonVariant.DesctructiveGhost}
                onClick={() => deleteMarginFilterModalProps.open({ marginFilterId: filter.id })}
              >
                <SiClose className="text-xl" />
                <span>{strings.delete}</span>
              </Button>
            </Box>
          ))}
          <Label>{strings.productFilters}</Label>
          {[...pack.composite.product].map(filter => (
            <Box className="flex justify-between items-center" key={filter.id}>
              <div className="flex items-center">
                <FilterIcon />
                {filter.category && <Criteria label="Category" value={filter.category?.name} />}
                {filter.manufacturer && <Criteria label="Manufacturer" value={filter.manufacturer?.name} />}
                {filter.modifier && <p className="font-medium">{filter.modifier}%</p>}
              </div>
              <Button
                variant={ButtonVariant.DesctructiveGhost}
                onClick={() => deleteProductFilterModalProps.open({ productFilterId: filter.id })}
              >
                <SiClose className="text-xl" />
                <span>{strings.delete}</span>
              </Button>
            </Box>
          ))}
          {[...pack.category.product].map(filter => (
            <Box className="flex justify-between items-center" key={filter.id}>
              <div className="flex items-center">
                <FilterIcon />
                {filter.category && <Criteria label="Category" value={filter.category?.name} />}
                {filter.modifier && <p className="font-medium">{filter.modifier}%</p>}
              </div>
              <Button
                variant={ButtonVariant.DesctructiveGhost}
                onClick={() => deleteProductFilterModalProps.open({ productFilterId: filter.id })}
              >
                <SiClose className="text-xl" />
                <span>{strings.delete}</span>
              </Button>
            </Box>
          ))}
          {[...pack.manufacturer.product].map(filter => (
            <Box className="flex justify-between items-center" key={filter.id}>
              <div className="flex items-center">
                <FilterIcon />
                {filter.manufacturer && <Criteria label="Manufacturer" value={filter.manufacturer?.name} />}
                {filter.modifier && <p className="font-medium">{filter.modifier}%</p>}
              </div>
              <Button
                variant={ButtonVariant.DesctructiveGhost}
                onClick={() =>
                  deleteProductFilterModalProps.open({ productFilterId: filter.id, filteredByManufacturer: true })
                }
              >
                <SiClose className="text-xl" />
                <span>{strings.delete}</span>
              </Button>
            </Box>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Page className="flex flex-col">
      <div className="flex w-full flex-1 flex-col overflow-hidden px-8 pt-8">
        <ManageFilters />
      </div>
    </Page>
  );
};
